import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useChannelManage } from '../../../hooks/useChannelManage';
import { useChannelContentManage } from '../../../hooks/useChannelContentManage';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { updateChannel as updateChannelApi } from '../../../api/marketplaceApi';
import type { Channel } from 'types/channel';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import StickyUnsavedBarMolecule from '../../molecules/marketplace/StickyUnsavedBarMolecule';
import ChannelManageNavRailMolecule, {
  type ChannelManageSection,
} from '../../molecules/marketplace/ChannelManageNavRailMolecule';
import ChannelItemsSectionOrganism from './ChannelItemsSectionOrganism';
import ChannelProfileSectionOrganism, {
  type ChannelProfileDraft,
} from './ChannelProfileSectionOrganism';
import ChannelBrandingSectionOrganism, {
  type ChannelBrandingDraft,
} from './ChannelBrandingSectionOrganism';
import ChannelAudienceSectionOrganism, {
  type AudienceDraft,
} from './ChannelAudienceSectionOrganism';
import ChannelReviewsSectionOrganism from './ChannelReviewsSectionOrganism';
import ChannelVerificationSectionOrganism from './ChannelVerificationSectionOrganism';
import ChannelDangerSectionOrganism from './ChannelDangerSectionOrganism';
import AddItemDialogMolecule from '../../molecules/marketplace/AddItemDialogMolecule';
import { SyndicateItemDialogOrganism } from './SyndicateItemDialogOrganism';
import './ChannelManageOrganism.scss';

/** Unified draft shape for the V3 manager, covering all editable sections. */
interface ChannelManageV3Draft extends ChannelProfileDraft, ChannelBrandingDraft, AudienceDraft {}

function toLocalizedString(
  map: Record<string, string>,
  defaultLocale: string
): { default: string; translations?: Record<string, string> } {
  const translations: Record<string, string> = {};
  for (const [locale, value] of Object.entries(map)) {
    if (locale !== defaultLocale && value.trim()) {
      translations[locale] = value;
    }
  }
  return {
    default: map[defaultLocale] ?? '',
    ...(Object.keys(translations).length > 0 ? { translations } : {}),
  };
}

function buildInitialDraft(channel: Channel): ChannelManageV3Draft {
  const defaultLocale = channel.default_locale ?? 'en-US';

  const derivedLocales: string[] =
    channel.locales && channel.locales.length > 0
      ? channel.locales
      : [defaultLocale, ...Object.keys(channel.name?.translations ?? {})].filter(
          (v, i, a) => a.indexOf(v) === i
        );

  const name_i18n: Record<string, string> = { [defaultLocale]: channel.name?.default ?? '' };
  for (const [locale, value] of Object.entries(channel.name?.translations ?? {})) {
    name_i18n[locale] = value;
  }

  const tagline_i18n: Record<string, string> = { [defaultLocale]: channel.tagline?.default ?? '' };
  for (const [locale, value] of Object.entries(channel.tagline?.translations ?? {})) {
    tagline_i18n[locale] = value;
  }

  const description_i18n: Record<string, string> = {
    [defaultLocale]: channel.description?.default ?? '',
  };
  for (const [locale, value] of Object.entries(channel.description?.translations ?? {})) {
    description_i18n[locale] = value;
  }

  return {
    locales: derivedLocales,
    defaultLocale,
    name_i18n,
    tagline_i18n,
    description_i18n,
    tags: channel.tags ?? [],
    scope: channel.scope,
    publisherSystemName: channel.publisher?.system_name ?? null,
    glyph: channel.glyph ?? '',
    glyphKind: channel.glyph_kind ?? 'letter',
    hue: channel.hue ?? 210,
    visibility: channel.visibility ?? 'private',
    isFeatured: channel.is_featured ?? false,
  };
}

const SCOPE_LABEL_KEYS: Record<string, string> = {
  center: 'marketplace.common.scope.center',
  organization: 'marketplace.common.scope.organization',
  metasphere: 'marketplace.common.scope.metasphere',
};

const STATUS_PILL: Record<
  string,
  { tone: 'success' | 'info' | 'warn' | 'danger'; icon: string; labelKey: string }
> = {
  approved: {
    tone: 'success',
    icon: 'pi pi-check-circle',
    labelKey: 'marketplace.channelManage.status.approved',
  },
  pending_review: {
    tone: 'warn',
    icon: 'pi pi-clock',
    labelKey: 'marketplace.channelManage.status.pendingReview',
  },
  rejected: {
    tone: 'danger',
    icon: 'pi pi-times-circle',
    labelKey: 'marketplace.channelManage.status.rejected',
  },
  draft: {
    tone: 'info',
    icon: 'pi pi-file-edit',
    labelKey: 'marketplace.channelManage.status.draft',
  },
  suspended: {
    tone: 'danger',
    icon: 'pi pi-ban',
    labelKey: 'marketplace.channelManage.status.suspended',
  },
};

export interface ChannelManageOrganismProps {
  channelId: string;
  onBack: () => void;
}

const ChannelManageOrganism: React.FC<ChannelManageOrganismProps> = ({ channelId, onBack }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const { channel, permissions, loading, error, refetch } = useChannelManage(channelId);
  const contentManage = useChannelContentManage(channelId);

  const [activeSection, setActiveSection] = useState<ChannelManageSection>('profile');
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showSyndicateDialog, setShowSyndicateDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialDraft = useMemo(
    () => (channel ? buildInitialDraft(channel) : null),
    [channel?.id]
  );
  const [draft, setDraft] = useState<ChannelManageV3Draft | null>(null);

  const effectiveDraft = draft ?? initialDraft;
  const isDirty = useMemo(() => {
    if (!effectiveDraft || !initialDraft) return false;
    return JSON.stringify(effectiveDraft) !== JSON.stringify(initialDraft);
  }, [effectiveDraft, initialDraft]);

  const update = useCallback((patch: Partial<ChannelManageV3Draft>) => {
    setDraft((prev) => {
      const base = prev ?? initialDraft ?? ({} as ChannelManageV3Draft);
      return { ...base, ...patch } as ChannelManageV3Draft;
    });
  }, [initialDraft]);

  const handleDiscard = useCallback(() => {
    setDraft(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!effectiveDraft || !channel || !organizationId) return;
    setSaving(true);
    try {
      if (apiClients) {
        await updateChannelApi(apiClients.schemaClient, channelId, {
          name: toLocalizedString(effectiveDraft.name_i18n, effectiveDraft.defaultLocale),
          tagline: toLocalizedString(effectiveDraft.tagline_i18n, effectiveDraft.defaultLocale),
          description: toLocalizedString(effectiveDraft.description_i18n, effectiveDraft.defaultLocale),
          tags: effectiveDraft.tags,
          locales: effectiveDraft.locales,
          defaultLocale: effectiveDraft.defaultLocale,
          scope: effectiveDraft.scope,
          publisherSystemName: effectiveDraft.publisherSystemName,
          glyph: effectiveDraft.glyph || null,
          glyphKind: effectiveDraft.glyphKind,
          hue: effectiveDraft.hue,
          visibility: effectiveDraft.visibility,
          isFeatured: effectiveDraft.isFeatured,
        });
      }
      await refetch();
      setDraft(null);
    } finally {
      setSaving(false);
    }
  }, [effectiveDraft, channel, apiClients, organizationId, channelId, refetch]);

  const handleAddItem = useCallback(() => setShowAddItemDialog(true), []);
  const handleItemAdded = useCallback(() => {
    setShowAddItemDialog(false);
    contentManage.refetch();
  }, [contentManage]);

  const handleSyndicateItem = useCallback(() => setShowSyndicateDialog(true), []);
  const handleItemSyndicated = useCallback(() => {
    setShowSyndicateDialog(false);
    contentManage.refetch();
  }, [contentManage]);

  if (loading && !channel) {
    return (
      <div className="channel-manage-v3">
        <button type="button" className="channel-manage-v3__back-btn" onClick={onBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          {t('marketplace.channelManage.backToChannel')}
        </button>
        <div className="channel-manage-v3__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>{t('marketplace.channelManage.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div className="channel-manage-v3">
        <button type="button" className="channel-manage-v3__back-btn" onClick={onBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          {t('marketplace.channelManage.backToChannel')}
        </button>
        <div className="channel-manage-v3__error">
          <p>{error ? t('marketplace.channelManage.errorLoading') : t('marketplace.channelManage.notFound')}</p>
          {error && (
            <button type="button" onClick={refetch}>
              {t('marketplace.common.retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const displayName = getLocalizedText(channel.name);
  const statusMeta = STATUS_PILL[channel.status];
  const scopeLabelKey = SCOPE_LABEL_KEYS[channel.scope];

  return (
    <div className="channel-manage-v3">
      {/* Back + breadcrumb */}
      <button type="button" className="channel-manage-v3__back-btn" onClick={onBack}>
        <i className="pi pi-arrow-left" aria-hidden="true" />
        {t('marketplace.channelManage.backToNamed', { name: displayName })}
      </button>

      <nav
        className="channel-manage-v3__breadcrumb"
        aria-label={t('marketplace.channelManage.breadcrumb.manage')}
      >
        <span className="channel-manage-v3__breadcrumb-link">
          {t('marketplace.channelManage.breadcrumb.marketplace')}
        </span>
        <span className="channel-manage-v3__breadcrumb-sep">{'›'}</span>
        <span className="channel-manage-v3__breadcrumb-link">
          {t('marketplace.channelManage.breadcrumb.channels')}
        </span>
        <span className="channel-manage-v3__breadcrumb-sep">{'›'}</span>
        <span className="channel-manage-v3__breadcrumb-link">{displayName}</span>
        <span className="channel-manage-v3__breadcrumb-sep">{'›'}</span>
        <strong className="channel-manage-v3__breadcrumb-current">
          {t('marketplace.channelManage.breadcrumb.manage')}
        </strong>
      </nav>

      {/* Identity bar */}
      <div className="channel-manage-v3__identity-bar">
        <ScopeCoinAtom
          scope={channel.scope}
          glyph={channel.glyph ?? null}
          glyphKind={channel.glyph_kind}
          channelName={displayName}
          hue={channel.hue}
          size="md"
          isSystem={channel.is_system}
          className="channel-manage-v3__identity-glyph"
        />
        <div className="channel-manage-v3__identity-info">
          <div className="channel-manage-v3__identity-title-row">
            <h1 className="channel-manage-v3__title">
              {t('marketplace.channelManage.manageTitle', { name: displayName })}
            </h1>
            {statusMeta && (
              <StatusBadgeAtom
                tone={statusMeta.tone}
                icon={statusMeta.icon}
                label={t(statusMeta.labelKey)}
                size="small"
              />
            )}
            {scopeLabelKey && (
              <ChipAtom
                label={t(scopeLabelKey)}
                className="channel-manage-v3__scope-chip"
              />
            )}
          </div>
          {channel.system_name && (
            <code className="channel-manage-v3__slug">
              {channel.scope === 'metasphere' && channel.publisher
                ? `/marketplace/${channel.publisher.slug}/${channel.system_name}`
                : `/${channel.scope}/channels/${channel.system_name}`}
            </code>
          )}
        </div>
        <button
          type="button"
          className="channel-manage-v3__view-public-btn"
        >
          <i className="pi pi-external-link" aria-hidden="true" />
          {t('marketplace.channelManage.viewPublicPage')}
        </button>
      </div>

      {/* Two-column layout */}
      <div className="channel-manage-v3__layout">
        <ChannelManageNavRailMolecule
          channel={channel}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          className="channel-manage-v3__nav-rail"
        />

        <div className="channel-manage-v3__content">
          {activeSection === 'profile' && effectiveDraft && (
            <ChannelProfileSectionOrganism
              channel={channel}
              draft={effectiveDraft}
              update={update}
            />
          )}
          {activeSection === 'items' && (
            <ChannelItemsSectionOrganism
              channelId={channelId}
              canManage={permissions.canManageContent}
              contentManage={contentManage}
              onAddItem={handleAddItem}
              onSyndicateItem={handleSyndicateItem}
            />
          )}
          {activeSection === 'branding' && effectiveDraft && (
            <ChannelBrandingSectionOrganism
              channel={channel}
              draft={effectiveDraft}
              update={update}
              onLogoChanged={refetch}
            />
          )}
          {activeSection === 'audience' && effectiveDraft && organizationId && (
            <ChannelAudienceSectionOrganism
              channel={channel}
              draft={effectiveDraft}
              update={update}
              organizationId={organizationId}
            />
          )}
          {activeSection === 'reviews' && (
            <ChannelReviewsSectionOrganism channelId={channelId} />
          )}
          {activeSection === 'verification' && (
            <ChannelVerificationSectionOrganism
              channel={channel}
              onChannelUpdated={refetch}
            />
          )}
          {activeSection === 'danger' && (
            <ChannelDangerSectionOrganism
              channel={channel}
              channelId={channelId}
              permissions={permissions}
              onChannelUpdated={refetch}
            />
          )}
        </div>
      </div>

      <StickyUnsavedBarMolecule
        isDirty={isDirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        className="channel-manage-v3__save-bar"
      />

      <AddItemDialogMolecule
        open={showAddItemDialog}
        channelId={channelId}
        onClose={() => setShowAddItemDialog(false)}
        onAdded={handleItemAdded}
      />

      <SyndicateItemDialogOrganism
        channelId={channelId}
        visible={showSyndicateDialog}
        onHide={() => setShowSyndicateDialog(false)}
        onSyndicated={handleItemSyndicated}
      />
    </div>
  );
};

ChannelManageOrganism.displayName = 'ChannelManageOrganism';

export default ChannelManageOrganism;
